import React, { useMemo } from 'react';
import RecentPosts from '../RecentPosts';
import styles from './styles.module.scss';
import { ComponentConsumerProps, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { ImageField, TextField } from '../../types/SitecoreAdapter';
import withErrorBoundary from '../../util/components/ErrorBoundary/withErrorBoundary';

// This is the shape that RecentPosts expects for each card.
type RelatedPostCard = {
  description: string;
  href: string;
  image: string;
  imageAlt: string;
  tag: string;
  title: string;
};

// Updated prop types based on the new data structure.
type RelatedPostsProps = ComponentConsumerProps & {
  fields: {
    heading: string
    relatedPosts: {
      cards: {
        targetItems: RelatedPostProps[];
      };
    };
    fallback: boolean
  };
};

// Each card from the backend now looks like this:
type RelatedPostProps = {
  relatedArticleItem: {
    targetItem: {
      thumbnail: {
        src: string, 
        alt: string,
        height: string,
        width: string
      };
      heading: TextField;
      url: string;
    };
  };
  titleOverride: TextField;
  thumbnailOverride: {
    src: string;
    alt: string;
    width?: string;
    height?: string;
    title?: string;
  }| null;
};

const RelatedPosts: React.FC<RelatedPostsProps> = ({
  fields: { relatedPosts, fallback, heading },
}) => {

  if (fallback) {
    console.log("[RelatedPosts] - Using fallback datasource");
  }

  // Pull the array of cards from the new data structure.
  const posts = relatedPosts?.cards?.targetItems || [];

  const headingText = () => {
    if (fallback) {
      return "Related posts:"
    }
    else return heading
  }

  // Map the backend data to the format required by RecentPosts.
  const cardsArray: RelatedPostCard[] = useMemo(() => {
    return posts.map((post: RelatedPostProps, i) => {
      const { relatedArticleItem, titleOverride, thumbnailOverride } = post;

      console.log(relatedArticleItem.targetItem.thumbnail)
      // Use the thumbnailOverride if available; otherwise, fall back to the article item's thumbnail.
      const image =
        thumbnailOverride?.src ||
        relatedArticleItem?.targetItem?.thumbnail?.src ||
        `/-/media/placeholders/default-card-placeholder-${i + 1}.webp`;

      const imageAlt =
        thumbnailOverride?.alt ||
        relatedArticleItem?.targetItem?.thumbnail?.alt ||
        'A default placeholder a related post.';

      // Use the titleOverride if provided; if not, use the heading value.
      const title =
        titleOverride?.value ||
        relatedArticleItem?.targetItem?.heading?.value ||
        'More help with insurance';

      // Get the URL from the related article item.
      const href = relatedArticleItem?.targetItem?.url || '/404';
      return {
        description: '',
        href,
        image,
        imageAlt,
        tag: '',
        title,
      };
    });
  }, [posts]);

  return (
      <RecentPosts
        fields={{
          title: headingText(),
          cards: cardsArray,
          seeMoreLink: '',
          seeMoreText: '',
        }}
        hideCardDescription={true}
        hideCardTag={true}
        styleOverride={{ postList: styles.recentPosts, wrapper: styles.wrapperOverride }}
        blogCardStyleOverride={{
          card: styles.recentPostsCard,
          link: styles.recentPostsLink,
        }}
      />
  );
};

export default withErrorBoundary(withSitecoreContext()(RelatedPosts), {
  component: 'RelatedPosts',
})